import PropTypes from "prop-types";
import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Custom select so the closed field and open options panel share the same
 * border, radius, and alignment (native <select> menus cannot be styled).
 *
 * Variants:
 * - pill  — filter toolbars (Outreach / Signals)
 * - field — form rows / modals
 */
export function FilterSelect({
  value,
  onChange,
  options,
  className,
  triggerClassName,
  menuClassName,
  variant = "pill",
  disabled = false,
  "aria-label": ariaLabel,
  "data-testid": testId,
  id,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const listId = useId();
  const selected = options.find((o) => o.value === value) || options[0] || null;
  const isPill = variant === "pill";

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={cn(
        "filter-select relative",
        isPill ? "inline-flex shrink-0" : "flex w-full",
        open && "is-open",
        className,
      )}
    >
      <button
        type="button"
        id={id}
        className={cn(
          "filter-select-trigger inline-flex items-center gap-2 border border-solid bg-white text-left cursor-pointer",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          isPill
            ? "rounded-[var(--radius-lg,16px)] border-[var(--slate-200,#E2E8F0)] px-[15px] py-[9px] text-[13.5px] font-medium text-[var(--slate-600,#475569)]"
            : "h-9 w-full justify-between rounded-[var(--radius-md,12px)] border-slate-200 px-3 text-[13px] text-slate-700",
          open && "border-[var(--slate-300,#CBD5E1)]",
          triggerClassName,
        )}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        data-testid={testId}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="filter-select-label min-w-0 truncate">
          {selected?.label ?? ""}
        </span>
        <ChevronDown
          size={14}
          aria-hidden
          className={cn(
            "filter-select-chevron shrink-0 text-[var(--slate-400,#94A3B8)] transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open ? (
        <div
          id={listId}
          aria-label={ariaLabel}
          role="listbox"
          className={cn(
            "filter-select-menu absolute left-0 z-40 m-0 list-none overflow-y-auto border border-solid bg-white p-1.5",
            "shadow-[0_12px_28px_rgba(15,23,42,0.12)]",
            "top-[calc(100%+6px)] min-w-full w-max max-w-[min(320px,90vw)] max-h-64",
            isPill
              ? "rounded-[var(--radius-lg,16px)] border-[var(--slate-200,#E2E8F0)]"
              : "rounded-[var(--radius-md,12px)] border-slate-200",
            menuClassName,
          )}
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <div
                key={String(opt.value)}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                className={cn(
                  "filter-select-option flex w-full cursor-pointer items-center border-0 bg-transparent text-left",
                  isPill
                    ? "rounded-[10px] px-3 py-2 text-[13.5px] text-[var(--slate-700,#334155)]"
                    : "rounded-md px-2.5 py-1.5 text-[13px] text-slate-700",
                  isSelected
                    ? "bg-[var(--indigo-50,#EEF2FF)] font-semibold text-[var(--indigo,#4F46E5)]"
                    : "hover:bg-[var(--slate-50,#F8FAFC)]",
                )}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(opt.value);
                    setOpen(false);
                  }
                }}
              >
                {opt.label}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

FilterSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.node.isRequired,
  })).isRequired,
  className: PropTypes.string,
  triggerClassName: PropTypes.string,
  menuClassName: PropTypes.string,
  variant: PropTypes.oneOf(["pill", "field"]),
  disabled: PropTypes.bool,
  "aria-label": PropTypes.string,
  "data-testid": PropTypes.string,
  id: PropTypes.string,
};
